import { useMemo, useState } from "react";
import { EmptyState } from "@/components/ui/empty-state";
import { EngagementToggle } from "@/components/ui/engagement-toggle";
import {
  useSalespersonMetrics,
  useSalespersonTouchpointTypes,
} from "@/hooks/api/use-salesperson-metrics";
import { useWipReportCardMetricsEnabled } from "@/hooks/use-feature-flags";
import { useSalespersonFilterState } from "@/hooks/use-salesperson-filter-state";
import { useTouchpointTypeSelection } from "@/hooks/use-touchpoint-type-selection";
import { SalespersonMetricCards } from "./metric-cards";
import { MetricDrilldown } from "./metric-drilldown";
import {
  DEFAULT_METRIC_ORDER,
  DRILLDOWN_LIMIT,
  HIDDEN_METRICS,
  type SalespersonMetric,
  WIP_METRICS,
} from "./salesperson-metrics-config";
import { TouchpointTypeSelector } from "./touchpoint-type-selector";

interface SalespersonScorecardProps {
  personId: string;
  includeBids?: boolean;
  onIncludeBidsChange?: (checked: boolean) => void;
}

export function SalespersonScorecard({
  personId,
  includeBids = false,
  onIncludeBidsChange,
}: SalespersonScorecardProps) {
  const [selectedMetric, setSelectedMetric] =
    useState<SalespersonMetric | null>(null);
  const wipMetricsEnabled = useWipReportCardMetricsEnabled();

  // Get filter state
  const {
    accountFilters,
    opportunityFilters,
    touchpointFilters,
    hasAccountFilters,
    hasOpportunityFilters,
    hasTouchpointFilters,
  } = useSalespersonFilterState("salespeople::explorer");

  // Auto mode support: compute effective types
  const { auto, hasOverride, selectedTypes } =
    useTouchpointTypeSelection(personId);
  const { data: availableTypes } = useSalespersonTouchpointTypes(personId);
  const defaultTypes = useMemo(() => availableTypes || [], [availableTypes]);
  const effectiveTouchpointTypes = useMemo(() => {
    if (auto) return defaultTypes;
    if (hasOverride) return selectedTypes; // may be intentionally empty
    return defaultTypes;
  }, [auto, hasOverride, selectedTypes, defaultTypes]);

  // Build the list of metrics to fetch
  const metricsToFetch = useMemo(
    () => [
      ...new Set([
        ...(wipMetricsEnabled
          ? DEFAULT_METRIC_ORDER
          : DEFAULT_METRIC_ORDER.filter(
              (metric) => !WIP_METRICS.includes(metric),
            )),
        ...HIDDEN_METRICS,
        // Add timing metrics for salesperson_active_days display
        "first_engagement_date",
        "last_engagement_date",
      ]),
    ],
    [wipMetricsEnabled],
  );

  // Build filter object for API calls
  const apiFilters = useMemo(() => {
    const filters = {
      ...(hasAccountFilters && { account_filters: accountFilters }),
      ...(hasOpportunityFilters && { opportunity_filters: opportunityFilters }),
      ...(hasTouchpointFilters && { touchpoint_filters: touchpointFilters }),
    };

    // Return undefined if no filters are set, to avoid sending empty object
    return Object.keys(filters).length > 0 ? filters : undefined;
  }, [
    accountFilters,
    opportunityFilters,
    touchpointFilters,
    hasAccountFilters,
    hasOpportunityFilters,
    hasTouchpointFilters,
  ]);

  // Fetch the metrics
  const {
    data: metricsData,
    isLoading,
    isError,
  } = useSalespersonMetrics({
    personId,
    touchpointTypes: effectiveTouchpointTypes,
    metrics: metricsToFetch,
    filters: apiFilters,
    includeBids,
  });

  // Create loading states for each metric
  const loadingStates = useMemo(() => {
    const states = {} as Record<
      SalespersonMetric,
      { isLoading: boolean; isError: boolean }
    >;
    metricsToFetch.forEach((metric) => {
      states[metric as SalespersonMetric] = {
        isLoading: isLoading,
        isError: isError || false,
      };
    });
    return states;
  }, [metricsToFetch, isLoading, isError]);

  return (
    <div className="space-y-4">
      <SalespersonMetricCards
        metrics={metricsData?.metrics}
        onMetricSelect={setSelectedMetric}
        loadingStates={loadingStates}
        leftControls={
          <div className="flex items-center gap-2">
            <TouchpointTypeSelector personId={personId} />
            <EngagementToggle
              checked={includeBids}
              onCheckedChange={(v) => onIncludeBidsChange?.(v)}
            />
          </div>
        }
        showBody={effectiveTouchpointTypes.length > 0}
        emptyBody={
          effectiveTouchpointTypes.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 space-y-6">
              <EmptyState
                title="Select touchpoint types"
                description="Select at least one touchpoint type to view metrics."
              />
            </div>
          ) : undefined
        }
      />

      {selectedMetric && (
        <MetricDrilldown
          open={true}
          onOpenChange={(open) => !open && setSelectedMetric(null)}
          personId={personId}
          metric={selectedMetric}
          touchpointTypes={effectiveTouchpointTypes}
          filters={apiFilters}
          includeBids={includeBids}
          metrics={metricsData?.metrics}
          loadingStates={loadingStates}
          drillDownLimit={DRILLDOWN_LIMIT}
        />
      )}
    </div>
  );
}
